<template>
    <section>
        <FormSection :title="`Re-Admission Information`" :full-height="false" :application="application">
            <template #content>
                <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                    <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                    <span class="mt-1 big-text">
                        <b>Loading Re-Admit Form...</b>
                    </span>
                </section>
                <section class="d-flex flex-column p-4" v-if="!isLoading">
                    <div class="container row col-12 m-0 p-1">
                        <div class="col-6 form-group">
                            <label for="schedule-date">
                                Re-Admission Date(Start of Care) <span class="text-danger">*</span>
                            </label>
                            <div class="input-group flex-nowrap">
                                <DatePicker :getDate="handleDateChange">
                                    <div class="input-group date datepickerInput">
                                        <input
                                            type="text"
                                            name="reAdmissionDate"
                                            class="form-control rounded-0 border-end-0"
                                            placeholder="MM/DD/YYYY"
                                            :value="formatDate(reAdmit.ReAdmissionDate)"
                                            @change="handleDateInputChange"
                                            @blur="v$.reAdmit.ReAdmissionDate.$touch"
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                        <span v-if="v$.reAdmit.ReAdmissionDate.$error" class="field-invalid"
                                            >Re-Admission Date is required.</span
                                        >
                                    </div>
                                </DatePicker>
                            </div>
                        </div>
                        <div class="col-6 form-group">
                            <label for="admission hour"> Admission Hour </label>
                            <div class="input-group flex-nowrap">
                                <TimePicker id="travel-start-time" @update-props="changeTime">
                                    <input
                                        placeholder="hh:mm AM"
                                        :class="`form-control`"
                                        type="text"
                                        autocomplete="off"
                                        maxlength="8"
                                        v-model="reAdmit.ReAdmissionHour"
                                    />
                                </TimePicker>
                            </div>
                        </div>
                    </div>
                    <div class="container row col-12 m-0 p-1" v-if="!isNonMedical">
                        <div class="col-6 form-group">
                            <label for="case-manager"> Case Manager <span class="text-danger">*</span></label>
                            <div class="input-group d-flex flex-col">
                                <select
                                    id="case-manager"
                                    class="form-select"
                                    v-model="reAdmit.CaseManagerId"
                                    @blur="v$.reAdmit.caseManagerId.$touch"
                                >
                                    <option :value="null">Select Case Manager</option>
                                    <option
                                        v-for="caseManager of caseManagersList"
                                        :value="caseManager.Id"
                                        :key="`${caseManager.Id}-${caseManager.FirstName}`"
                                    >
                                        {{ caseManager.DisplayName }}
                                    </option>
                                </select>
                                <span v-if="v$.reAdmit.CaseManagerId.$error" class="field-invalid"
                                    >Case Manager is required.</span
                                >
                            </div>
                        </div>
                        <div class="col-6 form-group">
                            <label for="assign-to-clinician">
                                Assign to Clinician <span class="text-danger">*</span></label
                            >
                            <div class="input-group d-flex flex-col">
                                <select
                                    id="assign-to-clinician"
                                    class="form-select"
                                    v-model="reAdmit.ClinicianId"
                                    @blur="v$.reAdmit.ClinicianId.$touch"
                                >
                                    <option :value="null">Select Clinician</option>
                                    <option
                                        v-for="clinician of clinicians"
                                        :key="`${clinician.Id}-${clinician.FirstName}`"
                                        :value="clinician.Id"
                                    >
                                        {{ clinician.DisplayName }}
                                    </option>
                                </select>
                                <span v-if="v$.reAdmit.ClinicianId.$error" class="field-invalid"
                                    >Clinician is required.</span
                                >
                            </div>
                        </div>
                    </div>
                    <div class="container row col-12 m-0 p-1">
                        <div class="col-6 form-group">
                            <label for="physician">Physician </label>
                            <div class="input-group flex-nowrap">
                                <select id="physician" class="form-select" v-model="reAdmit.PhysicianId">
                                    <option :value="null">Select Physician</option>
                                    <option
                                        v-for="physician of physicianList"
                                        :key="`${physician.Id}-${physician.FirstName}`"
                                        :value="physician.Id"
                                    >
                                        {{ physician.DisplayName }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="col-6 form-group">
                            <label for="referral-date"> Referral Date <span class="text-danger">*</span></label>
                            <div class="input-group flex-nowrap">
                                <DatePicker :getDate="handleDateChange">
                                    <div class="input-group date datepickerInput">
                                        <input
                                            type="text"
                                            name="referralDate"
                                            class="form-control rounded-0 border-end-0"
                                            placeholder="MM/DD/YYYY"
                                            :value="formatDate(reAdmit.ReferralDate)"
                                            @change="handleDateInputChange"
                                            @blur="v$.reAdmit.ReferralDate.$touch"
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                        <span v-if="v$.reAdmit.ReferralDate.$error" class="field-invalid"
                                            >Referral Date is required.</span
                                        >
                                    </div>
                                </DatePicker>
                            </div>
                        </div>
                    </div>
                    <div class="d-flex justify-content-center mt-3">
                        <button class="btn btn-primary" @click="reAdmitPatient()">Re-Admit</button>
                        <button type="button" class="ms-1 btn btn-md btn-outline-secondary" @click="handleCloseForm()">
                            Close
                        </button>
                    </div>
                </section>
            </template>
        </FormSection>
    </section>
</template>

<script lang="ts" src="./HcReadmit.ts"></script>
